import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { AsyncRuleFunction, Configurator, CToken, eRuleType, IJsResult, MasterRuleFunction, RuleContainer, SyncRuleFunction, SvgViewer, Html } from "@models";
import { KPromise } from "@rules";
import { Logger } from "@tools";
import * as Q from "q";


export interface IRuleService {
    runRuleTypeAsync<T>(
        config: Configurator,
        ruleType: string,
        args: T
    ): ng.IPromise<IJsResult<T>>;
}

@NgService({
    token: Token.RuleService,
    dependencies: [
        Token.$Q,
    ]
})
export class RuleService implements IRuleService {
    constructor(
        private $q: ng.IQService
    ) {

    }
    private _masterRuleCache: { [hash: string]: MasterRuleFunction<any> } = {};

    public getConfiguratorHash(config: Configurator) {
        return `${config.idProduct || 0}-${config.idScene || 0}-${config.idQuoteHeader || 0}-${config.id}`;
    }
    public getRuleName(rc: RuleContainer) {
        return `rule-${rc.id}`;
    }
    public getRuleHeader(rc: RuleContainer) {
        let desc = `${rc.ruleType.toSentence()} Rule`;
        if (rc.ruleType.equalsAny(eRuleType.field, eRuleType.action, eRuleType.function, eRuleType.optionFilter)) {
            desc += " - " + rc.name;
        }
        let stars = "*".repeat(Math.max(desc.length - 1, 70));
        return `\r\n/${stars}\r\n * ${desc}\r\n ${stars}/\r\n`;
    }
    public getConfiguratorMasterRule(config: Configurator): MasterRuleFunction<any> {
        let hash = this.getConfiguratorHash(config);
        if (!this._masterRuleCache.hasOwnProperty(hash)) {
            let globalRc = config.ruleContainers.find(rc => rc.ruleType.isEqual(eRuleType.global));
            let js = globalRc ? globalRc.js + "\r\n\r\n" : "";
            let globalEventRc = config.ruleContainers.find(rc => rc.ruleType.isEqual(eRuleType.globalEvent));
            js += globalEventRc ? globalEventRc.js + "\r\n\r\n" : "";
            js += "var _ruleDb = {\r\n";
            let ruleContainers = config.ruleContainers
                .concat(config.actions)
                .concat(config.functions)
                .concat(config.getChildrenOfType(CToken.Field, CToken.Button, CToken.SvgViewer, CToken.NestedSet, CToken.RuleContainer, CToken.Html))
                .concat(config.optionFilters);
            config.getChildrenOfType<SvgViewer>(CToken.SvgViewer).forEach(svg => ruleContainers = ruleContainers.concat(svg.ruleContainers));
            config.getChildrenOfType<Html>(CToken.Html).forEach(html => ruleContainers = ruleContainers.concat(html.ruleContainers));
            config.referencedConfigurators.forEach(refConfig => ruleContainers = ruleContainers.concat(refConfig.ruleContainers));

            ruleContainers.forEach(rc => {
                if (rc.ruleType != eRuleType.global && rc.js) {
                    js += this.getRuleHeader(rc);
                    js += `"${this.getRuleName(rc)}": `;
                    var ruleJs = "\r\n\t" + rc.js.split("\n").join("\r\n\t")
                    if (rc.ruleType == eRuleType.optionFilter) {
                        js += "function(){\r\n" + ruleJs + "\r\n},";
                    } else {
                        js += "function(){return Q.fcall(function () {\r\n" + ruleJs + "\r\n})},";
                    }
                }
            });

            js += "};\r\n\r\n";
            js += "return _ruleDb[e.ruleName]();";

            this._masterRuleCache[hash] = new Function("Q", "e", js) as SyncRuleFunction<any>;
        }
        return this._masterRuleCache[hash];
    }

    public compileAsyncRule<T>(js: string): AsyncRuleFunction<T> {
        // tab out the code
        js = "\t\t" + js.split("\n").join("\r\n\t\t");

        // wrap the js.  We use Q.when().then(code) to make sure that errors are propogated to our promise error handler
        let rule = "\t" + "return Q.fcall(function () {\n" +
            js + "\n" +
            "\t})\n";

        return new Function("Q", "e", rule) as AsyncRuleFunction<T>;
    }

    public runRuleAsync<T>(parameters, fn: MasterRuleFunction<T>): ng.IPromise<IJsResult<T>> {
        let promise = (fn(Q, parameters) as ng.IPromise<any>)
            .then(() => {
                return {
                    parameters,
                    hasError: false,
                    error: null
                } as IJsResult<T>
            }, error => {
                return {
                    parameters,
                    hasError: true,
                    error
                } as IJsResult<T>;
            });

        return promise;
    }

    public runJsAsync<T>(parameters: T, js: string): ng.IPromise<IJsResult<T>> {
        var fn = this.compileAsyncRule<T>(js);
        return this.runRuleAsync(parameters, fn);
    }


    public compileSyncRule<T>(js: string): SyncRuleFunction<T> {
        return new Function("e", js) as SyncRuleFunction<T>;
    }

    /**
     * runs the rule synchronously... Only for use in special circumstances if you know what you're doing!
     */
    public runRuleSync<T>(parameters: T, fn: MasterRuleFunction<T>): IJsResult<T> {
        let result: IJsResult<T> = {};
        try {
            fn(Q, parameters);
        } catch (err) {
            result.hasError = true;
            result.error = err.message;
        }
        result.parameters = parameters;

        return result;
    }

    public runJsSync<T>(parameters: T, js: string): IJsResult<T> {
        let fn = this.compileSyncRule<T>(js);
        return this.runRuleSync(parameters, fn);
    }


    /**
     * runs the rule of a rule container, compiling and caching it along the way
     * @param ruleContainer 
     * @param args 
     */
    public runRuleContainerAsync<T>(
        ruleContainer: RuleContainer,
        args: T
    ): ng.IPromise<IJsResult<T>> {
        if (ruleContainer && ruleContainer.js) {
            let masterRule = this.getConfiguratorMasterRule(ruleContainer.$parentConfigurator);
            args["ruleName"] = this.getRuleName(ruleContainer);
            
            let start = Logger.logRuleStart(ruleContainer.$parentConfigurator.name, ruleContainer.ruleType);
            let promise = this.runRuleAsync<T>(args, masterRule);
            if (Logger.writeRuleCycleLogs) {
                promise = promise.then(result => {
                    Logger.logRuleEnd(ruleContainer.$parentConfigurator.name, ruleContainer.ruleType, start);
                    return result;
                });
            }
            return promise;
        } else { // if there is no text in the rule, then don't bother running it
            let result: IJsResult<T> = {
                hasError: false,
                parameters: args
            };
            return (new KPromise(result)) as any;
        }
    }

    public runRuleContainerSync<T>(
        ruleContainer: RuleContainer,
        args: T
    ): IJsResult<T> {
        if (ruleContainer && ruleContainer.js) {
            let masterRule = this.getConfiguratorMasterRule(ruleContainer.$parentConfigurator);
            args["ruleName"] = this.getRuleName(ruleContainer);
            return this.runRuleSync<T>(args, masterRule);
        } else { // if there is no text in the rule, then don't bother running it
            let result: IJsResult<T> = {
                hasError: false,
                parameters: args
            };
            return result;
        }
    }

    // /**
    //  * runs the rule of a rule container, compiling and caching it along the way
    //  * @param ruleContainer 
    //  * @param args 
    //  */
    // public runRuleContainerAsync<T>(
    //     ruleContainer: RuleContainer,
    //     args: T
    // ): ng.IPromise<IJsResult<T>> {
    //     // find the cached fn, if it's already been compiled
    //     if (ruleContainer && ruleContainer.js) {
    //         // find or cache the compiled fn for perf
    //         ruleContainer.$asyncFn = ruleContainer.$asyncFn || this.compileAsyncRule<T>(ruleContainer.$parentConfigurator.getRuleJsFromContainer(ruleContainer));
    //         return this.runRuleAsync<T>(args, ruleContainer.$asyncFn);
    //     } else { // if there is no text in the rule, then don't bother running it
    //         let result: IJsResult<T> = {
    //             hasError: false,
    //             parameters: args
    //         };
    //         return (new KPromise(result)) as any;
    //     }
    // }

    // public runRuleContainerSync<T>(
    //     ruleContainer: RuleContainer,
    //     args: T
    // ): IJsResult<T> {
    //     // find the cached fn, if it's already been compiled
    //     if (ruleContainer && ruleContainer.js) {
    //         // find or cache the compiled fn for perf
    //         ruleContainer.$syncFn = ruleContainer.$syncFn || this.compileSyncRule<T>(ruleContainer.$parentConfigurator.getRuleJsFromContainer(ruleContainer));
    //         return this.runRuleSync<T>(args, ruleContainer.$syncFn);
    //     } else { // if there is no text in the rule, then don't bother running it
    //         let result: IJsResult<T> = {
    //             hasError: false,
    //             parameters: args
    //         };
    //         return result;
    //     }
    // }


    public runRuleTypeAsync<T>(
        config: Configurator,
        ruleType: string,
        args: T
    ): ng.IPromise<IJsResult<T>> {
        let rc = config.ruleContainers.find(rc => {
            return rc.ruleType.isEqual(ruleType);
        });
        return this.runRuleContainerAsync(rc, args);
    }

    public runActionRuleByIdAsync<T>(
        config: Configurator,
        actionId: string,
        args: T
    ): ng.IPromise<IJsResult<T>> {
        let container = config.actions.find(a => a.id == actionId);

        return this.runRuleContainerAsync(container, args);
    }
}
